import { IMutation, InviteStatus, IQuery, ISubscription } from '~/client/graph'
import { GetAnalyticsSettingByProjectIdDocument } from '~/client/graph/operations/generated/AnalyticsSettings.generated'
import { GetPresentationUserTokenDocument } from '~/client/graph/operations/generated/Auth.generated'
import { DeliveriesByProjectIdDocument } from '~/client/graph/operations/generated/Deliveries.generated'
import { GetUploadLogsDocument } from '~/client/graph/operations/generated/FileUploader.generated'
import {
  GetPresentationSettingsDocument,
  ListenToPresentationSettingsDocument,
} from '~/client/graph/operations/generated/PresentationSettings.generated'
import { DownloadUsersCsvByProjectDocument } from '~/client/graph/operations/generated/Users.generated'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopRootStore from '~/client/src/desktop/stores/DesktopRoot.store'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import {
  getJHPRedirectUrl,
  isProjectBasedPath,
  isSignUpPageRequested,
} from '~/client/src/shared/constants/commonRoutes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Project from '~/client/src/shared/models/Project'
import * as e from '~/client/src/shared/stores/EventStore/eventConstants'
import { shouldPromptForPassword } from '~/client/src/shared/stores/EventStore/handlers'
import getIntegrationsConfigName from '~/client/src/shared/utils/getIntegrationsConfigName'

import { generateAndSendDesktopNotifications } from '../desktopNotificationsUtils'
import registerDesktopChangeEvents from './desktopChangeEvents'

export default function (
  eventsStore: DesktopEventStore,
  rootStore: DesktopRootStore,
) {
  eventsStore.on(e.INIT_APP_SELECT_PROJECT, state => {
    const [firstProjectDto] = rootStore.projectsStore.list
    if (firstProjectDto) {
      state.activeProject = Project.fromDto(firstProjectDto)
    }

    return {
      dispatch: [e.GET_AUTH_USER_PROJECT],
    }
  })

  /**
   * Complete initialization if a project was located
   */
  eventsStore.on(e.INIT_APP_3, state => {
    state.loading.set(e.INIT_APP_2, false)

    if (!state.activeProject.id) {
      return
    }

    state.delivery.compactView.activeDate =
      rootStore.projectDateStore.startOfDay(new Date())

    rootStore.clearOutProjectData()
    rootStore.clearAllFilters()
    rootStore.clearAllCustomFilters()

    const dispatchN: any[] = [
      [e.GET_PROJECT_CONFIG],
      [
        e.GET_CONFIGURATION,
        getIntegrationsConfigName(state.activeProject.id),
        e.PROJECT_INTEGRATIONS_CONFIGS_RECEIVED,
      ],
      [e.LOAD_AND_LISTEN_TO_MATERIAL_OPTIONS],
      [e.LOAD_AND_LISTEN_TO_PROJECT_ADDRESS],
      [e.LISTEN_TO_PROJECT],
      [e.LOAD_AND_LISTEN_TO_PROJECT_COLORING_OPTIONS],
      [e.LOAD_AND_LISTEN_TO_DELIVERY_CONFIGURATIONS],
      [e.LOAD_AND_LISTEN_TO_WORKFLOWS_CONFIGURATIONS],
      [e.LOAD_AND_LISTEN_TO_LOGISTICS_CONFIGURATIONS],
      [e.LOAD_AND_LISTEN_TO_ZONEMAP_THRESHOLDS],
      [e.LOAD_AND_LISTEN_TO_PROJECT_HISTORY],
      [e.LOAD_AND_LISTEN_TO_WORKFLOW_CATEGORIES],
      [e.LOAD_AND_LISTEN_TO_WORKFLOW_TYPES],
      [e.REGISTER_SERVICE_WORKER],
      [e.GET_PROJECT_MEMBERS],
      [e.LOAD_AND_LISTEN_TO_OPERATION_RULES],
      [e.LOAD_AND_LISTEN_TO_PROJECT_TEAMS],
      [e.LOAD_AND_LISTEN_TO_PROJECT_ROLES],
      [e.LOAD_AND_LISTEN_TO_PROJECT_TRADES],
      [e.LOAD_AND_LISTEN_TO_SITE_PERMIT_INSPECTIONS],
      [e.LOAD_AND_LISTEN_TO_SITE_PERMIT_INSPECTION_CHANGES],
      [e.LOAD_LOCATIONS_AND_LISTEN_CHANGES],
      [e.LISTEN_TO_WORKFLOW_LOCATION_RESTRICTION],
      [e.LOAD_RESOURCE_SCHEMAS],
      [e.LOAD_AND_LISTEN_TO_COMPANIES],
      [e.LOAD_AND_LISTEN_TO_USER_PROJECTS],
      [e.LOAD_AND_LISTEN_TO_SITEMAPS],
      [e.LOAD_AND_LISTEN_TO_GLOBE_VIEWS],
      [e.LISTEN_TO_SITEMAP_ITEMS_DATA],
      [e.LISTEN_TO_GLOBE_VIEW_ITEMS_DATA],
      [e.LOAD_AND_LISTEN_TO_BASEMAPS],
      [e.LOAD_AND_LISTEN_TO_SITEMAP_ITEMS],
      // Markups load lazily per view; on (re)init just reset + start the subscription.
      [e.CLEAR_MARKUPS],
      [e.LISTEN_TO_MARKUPS],
      [e.LOAD_AND_LISTEN_TO_MESSAGES],
      [e.LOAD_AND_LISTEN_TO_NOTIFICATIONS],
      [e.LOAD_AND_LISTEN_TO_PHOTOS],
      [e.LOAD_AND_LISTEN_TO_SCANNERS],
      [e.LOAD_AND_LISTEN_TO_SCAN_HISTORIES],
      [e.LOAD_AND_LISTEN_TO_SITE_PERMITS_FOLLOWINGS],
      [e.LOAD_AND_LISTEN_TO_SITE_PERMIT_ASSIGNMENTS],
      [e.LOAD_CLOSURES_AND_LISTEN_CHANGES],
      [e.LOAD_AND_LISTEN_TO_MATERIAL_CONFIGURATION],
      [e.LISTEN_TO_USERS],
      [e.UPDATE_USER_PREFERENCES],
      [e.LOAD_AND_LISTEN_TO_PRESENTATION_SETTINGS],
      [e.LOAD_AND_LISTEN_TO_MATERIAL_CATEGORIES],
      [e.LOAD_AND_LISTEN_TO_MATERIALS],
      [e.GET_SHARED_SITE_PERMITS],
      [e.LISTEN_TO_SHARED_SITE_PERMITS],
    ]

    // Events that must complete before desktop push-toasts can be generated.
    // Always wait for the shared notification pool; if deliveries are enabled,
    // also wait for the domain stores that toast content depends on.
    const toastWaitEvents = []
    toastWaitEvents.push(e.NOTIFICATIONS_RECEIVED)

    dispatchN.push([e.CHART_LOADED_AND_INITIALIZED])

    if (!state.isDeliveriesDisabled) {
      dispatchN.push([e.LOAD_AND_LISTEN_TO_DELIVERY_FIELDS_CONFIGURATIONS])
    }

    if (!state.isTilesetsDisabled) {
      dispatchN.push(
        [e.LOAD_AND_LISTEN_TO_TILESETS],
        [e.LOAD_AND_LISTEN_TO_REMOTE_TILESETS],
      )
    }

    if (!state.projectTypeOptions.isDeliveriesDisabled) {
      dispatchN.push([e.LOAD_AND_LISTEN_TO_ALL_DELIVERY_ATTRIBUTES])
    }

    if (!state.projectTypeOptions.isFormsDisabled) {
      dispatchN.push([e.GET_WORKFLOW_CUSTOM_FILTERS])
    }

    if (!state.projectTypeOptions.isDeliveriesDisabled) {
      dispatchN.push(
        [e.LISTEN_AND_LOAD_DELIVERIES],
        [e.GET_DELIVERY_CUSTOM_FILTERS],
        [e.LOAD_AND_LISTEN_TO_DELIVERY_ASSIGNMENTS],
        [e.LOAD_AND_LISTEN_TO_DELIVERIES_FOLLOWINGS],
        [e.LISTEN_TO_RECURRING_DELIVERIES_SETTING],
        [e.GET_SHARED_DELIVERIES],
        [e.LISTEN_TO_SHARED_DELIVERIES],
      )
      toastWaitEvents.push(
        e.LOAD_DELIVERIES_SUCCESS,
        e.LOAD_LOCATIONS_SUCCESS,
        e.COMPANIES_RECEIVED,
      )
    } else {
      rootStore.deliveriesStore.isDataReceived = true
    }

    dispatchN.unshift([e.LOAD_AND_LISTEN_TO_NOTIFICATIONS_COUNT])

    if (!state.isAnalyticsDisabled) {
      dispatchN.push([e.LOAD_ANALYTICS_SETTING])
    }

    if (state.userActiveProjectSettings) {
      state.didInitialize = true

      if (state.userActiveProjectSettings.isPending) {
        state.userActiveProjectSettings.setInviteStatus(InviteStatus.Accepted)

        dispatchN.push([
          e.SAVE_USER_PROJECTS,
          [state.userActiveProjectSettings.toDto()],
        ])
      }
    } else {
      state.didInitialize = false
    }

    // STX1-1057: a first-time user whose domain declares no SSO rule has a
    // password as their only possible credential. Prompt for one now — once
    // their 3-day invite key expires, an un-prompted user has no way back in.
    if (shouldPromptForPassword(rootStore)) {
      dispatchN.push([e.DISPLAY_SAVE_PASSWORD])
    }

    return {
      dispatchN,
      flow: {
        startWith: [],
        rules: [
          {
            when: 'all',
            events: toastWaitEvents,
            dispatch: [e.GENERATE_DESKTOP_TOASTS],
            successArgs: [],
          },
        ],
      },
    }
  })

  eventsStore.on(
    e.PROJECT_TYPE_OPTIONS_RECEIVED,
    (state, { projectTypeOptions }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_PROJECT_TYPE_OPTIONS, false)

      state.projectTypeOptions =
        projectTypeOptions.data[0] || state.getDefaultProjectTypeOptions()

      return {
        dispatch: [e.LISTEN_TO_PROJECT_TYPE_OPTIONS],
      }
    },
  )

  eventsStore.on(e.GET_UPLOAD_LOGS, state => {
    state.loading.set(e.GET_UPLOAD_LOGS, true)

    return {
      graphQuery: {
        query: GetUploadLogsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.UPLOAD_LOGS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.GET_UPLOAD_LOGS],
      },
    }
  })

  eventsStore.on(e.UPLOAD_LOGS_RECEIVED, (state, { getUploadLogs }: IQuery) => {
    state.loading.set(e.GET_UPLOAD_LOGS, false)
    rootStore.uploadLogsStore.receiveList(getUploadLogs)
  })

  eventsStore.on(
    e.GET_PROJECT_MEMBERS_SUCCESS,
    (state, { users, deletedUsers }) => {
      state.loading.set(e.GET_PROJECT_MEMBERS, false)

      rootStore.projectMembersStore.receiveList(users.data)
      rootStore.projectMembersStore.receiveDeletedList(deletedUsers)
    },
  )

  eventsStore.on(e.INIT_PROJECT_INTEGRATIONS, () => {
    rootStore.concreteDirectIntegrationStore.init()
    rootStore.maturixProjectsStore.init()
    rootStore.procoreIntegrationStore.init()
  })

  eventsStore.on(
    e.GET_USERS_FROM_PROCORE,
    (state, callbackSuccess, callbackError) => {
      return {
        cmd: {
          cmd: {
            execute: () => rootStore.procoreService.getPopulatedCompanies(),
          },
          onSuccess: [e.RUN_CALLBACK, callbackSuccess],
          onError: [e.RUN_CALLBACK, callbackError],
        },
      }
    },
  )

  eventsStore.on(e.CHART_LOADED_AND_INITIALIZED, state => {
    if (state.userActiveProjectSettings?.isPresentationUser) {
      return {
        dispatchN: [[e.RESET_ALL_FILTERS], [e.START_PRESENTATION]],
      }
    } else {
      return {
        dispatch: [e.RESET_ALL_FILTERS],
      }
    }
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_NOTIFICATIONS_COUNT, () => {
    return {
      dispatch: [e.LISTEN_TO_NOTIFICATIONS_COUNT],
    }
  })

  eventsStore.on(e.GENERATE_DESKTOP_TOASTS, state => {
    if (!state.pushNotificationEntityId) {
      const unread = rootStore.notificationsStore.all.filter(
        n => !n.wasRead && !n.isArchived,
      )
      generateAndSendDesktopNotifications(unread, rootStore)
    }
  })

  eventsStore.on(e.GET_PRESENTATION_USER_TOKEN, state => {
    state.loading.set(e.GET_PRESENTATION_USER_TOKEN, true)

    return {
      graphMutation: {
        mutation: GetPresentationUserTokenDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.GET_PRESENTATION_USER_TOKEN_SUCCESS],
        onError: [e.REQUEST_ERROR, e.GET_PRESENTATION_USER_TOKEN],
      },
    }
  })

  eventsStore.on(
    e.GET_PRESENTATION_USER_TOKEN_SUCCESS,
    (state, { getPresentationUserToken: inviteKey }: IMutation) => {
      state.loading.set(e.GET_PRESENTATION_USER_TOKEN, false)

      return {
        dispatch: [e.LOGIN_WITH_INVITE_KEY, inviteKey],
      }
    },
  )

  eventsStore.on(e.START_PRESENTATION, () => {
    return {
      flow: {
        startWith: [[e.LOAD_AND_LISTEN_TO_PRESENTATION_SETTINGS]],
        rules: [
          {
            when: 'on',
            event: e.PRESENTATION_SETTINGS_RECEIVED,
            dispatch: [e.PLAY_PRESENTATION],
            shouldTerminate: true,
          },
        ],
      },
    }
  })

  eventsStore.on(e.PLAY_PRESENTATION, () => {
    rootStore.presentationModeStore.playPresentation()
  })

  eventsStore.on(e.LOAD_AND_LISTEN_TO_PRESENTATION_SETTINGS, state => {
    state.loading.set(e.LOAD_AND_LISTEN_TO_PRESENTATION_SETTINGS, true)
    return {
      graphQuery: {
        query: GetPresentationSettingsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.PRESENTATION_SETTINGS_RECEIVED],
        onError: [e.REQUEST_ERROR, e.LOAD_AND_LISTEN_TO_PRESENTATION_SETTINGS],
      },
    }
  })

  eventsStore.on(
    e.PRESENTATION_SETTINGS_RECEIVED,
    (state, { presentationSettings }: IQuery) => {
      state.loading.set(e.LOAD_AND_LISTEN_TO_PRESENTATION_SETTINGS, false)
      state.updatePresentationSettings({ ...presentationSettings.data[0] })

      return {
        dispatch: [e.LISTEN_TO_PRESENTATION_SETTINGS],
      }
    },
  )

  eventsStore.on(e.LISTEN_TO_PRESENTATION_SETTINGS, state => {
    return {
      graphSubscription: {
        query: ListenToPresentationSettingsDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onData: [e.PRESENTATION_SETTINGS_UPDATED],
        onSuccess: [e.NO_EFFECT],
        onError: [e.REQUEST_ERROR, e.LISTEN_TO_PRESENTATION_SETTINGS],
      },
    }
  })

  eventsStore.on(
    e.PRESENTATION_SETTINGS_UPDATED,
    (state: DesktopInitialState, { presentationSettings }: ISubscription) => {
      state.updatePresentationSettings(presentationSettings.item)
    },
  )

  eventsStore.on(e.TRY_UNAUTHORIZED_MODE, state => {
    // QR-code signup is a public path — TRY_UNAUTHORIZED_MODE_RESULT (shared)
    // bounces to login when isUnauthorizedMode stays false, so flip it for
    // signup before the JHP check.
    if (isSignUpPageRequested()) {
      state.isUnauthorizedMode = true
    }

    const jhpUrl = getJHPRedirectUrl(state.initPathname, state.initProjectCode)
    if (jhpUrl) {
      window.location.href = jhpUrl
      return
    }

    return { dispatch: [e.TRY_UNAUTHORIZED_MODE_RESULT] }
  })

  eventsStore.on(e.GO_TO_AVAILABLE_PROJECT, state => {
    const availableProjectCode =
      rootStore.projectsStore.getAvailableProjectCode()

    if (!availableProjectCode) {
      return alert(Localization.translator.thereIsNoProject)
    }

    state.initProjectCode = availableProjectCode

    // Same branching shape as ACTIVATE_PROJECT_SUCCESS post-fix
    // (desktopChangeEvents.ts): if we were on a /project/ path keep the same
    // view and swap the project segment, otherwise hand off to the `/`
    // catch-all so redirectToDefaultRoute picks the right landing.
    if (isProjectBasedPath()) {
      rootStore.common.displaySameViewWithUpdatedProjectId()
    } else {
      rootStore.common.displayDefaultView()
    }

    return { dispatch: [e.INIT_APP_2] }
  })

  eventsStore.on(e.DOWNLOAD_PROJECT_MEMBERS, (state, onSuccess, onError) => {
    state.loading.set(e.DOWNLOAD_PROJECT_MEMBERS, true)

    return {
      graphMutation: {
        mutation: DownloadUsersCsvByProjectDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [
          e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
          e.DOWNLOAD_PROJECT_MEMBERS,
          onSuccess,
        ],
        onError: [
          e.COMPLETE_REQUEST_AND_RUN_CALLBACK,
          e.DOWNLOAD_PROJECT_MEMBERS,
          onError,
        ],
      },
    }
  })

  eventsStore.on(e.LOAD_DELIVERIES, state => {
    state.loading.set(e.LOAD_DELIVERIES, true)

    return {
      graphQuery: {
        query: DeliveriesByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.LOAD_DELIVERIES_SUCCESS],
        onError: [e.REQUEST_ERROR, e.LOAD_DELIVERIES],
      },
    }
  })

  eventsStore.on(e.RESET_MATERIALS_FILTERS, (state: DesktopInitialState) => {
    Object.values(state.materialFilters.fieldsMap || {}).forEach(field =>
      field.clear(),
    )
  })

  eventsStore.on(e.LOAD_ANALYTICS_SETTING, (state: DesktopInitialState) => {
    state.loading.set(e.LOAD_ANALYTICS_SETTING, true)

    return {
      graphQuery: {
        query: GetAnalyticsSettingByProjectIdDocument,
        variables: {
          projectId: state.activeProject.id,
        },
        onSuccess: [e.ANALYTICS_SETTING_RECEIVED],
        onError: [e.REQUEST_ERROR],
      },
    }
  })

  eventsStore.on(
    e.ANALYTICS_SETTING_RECEIVED,
    (state, { analyticsSettings }: IQuery) => {
      state.loading.set(e.LOAD_ANALYTICS_SETTING, false)

      rootStore.analyticsSettingsStore.receiveData(analyticsSettings)
    },
  )

  registerDesktopChangeEvents(eventsStore, rootStore)
}
